/**
 * A ficha de um conteúdo: o que ele classifica, o que ele aponta, e as ações.
 *
 * **Alterar aqui reproduz o gesto do sistema antigo**, que é o que economiza o
 * dia de quem cadastra: os seis campos de classificação aceitam **vários**
 * valores, e o resultado é uma linha por combinação. A primeira sobrescreve
 * este registro; as demais viram registros novos.
 *
 * Para as demais eu uso a *duplicação*, não o cadastro em lote — a duplicação
 * herda modelo, versão e origem do original **e copia os itens**. Pelo lote, as
 * cópias sairiam sem o material apontado, que é justamente o que interessa.
 *
 * **Os anexos moram aqui, e não no cadastro.** O arquivo é renomeado para
 * `{dadoId}_{ordem}`, então precisa do registro já criado para ter nome. É
 * também onde se vê o que o sistema antigo nunca mostrou: o item cadastrado cujo
 * arquivo não está no servidor. A ApiConteudo responde a um arquivo ausente com
 * um PNG transparente de 1×1, e a tela antiga mostrava isso como branco; aqui
 * volta 404 com a frase inteira.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import {
  usarAlterarConteudo, usarConteudo, usarDuplicarConteudo, usarExcluirConteudo,
  siglaDoIdioma, TIPOS_DE_ITEM,
  type ItemDoConteudo,
} from "@/api/conteudo"
import {
  baixarArquivoDoConteudo, usarArquivosDoConteudo, usarEnviarArquivoDoConteudo,
  usarExcluirArquivoDoConteudo, type ArquivoDoConteudo,
} from "@/api/arquivosDoConteudo"
import { filtrarPelaMarca, usarCatalogosDeConteudo } from "@/api/catalogosDeConteudo"
import { ErroDaApi } from "@/api/erros"
import { Botao, Seletor, mensagem, type Concluir } from "./Conteudo"
import { MultiSelecao, combinacoes, type Combinacao } from "./MultiSelecao"

export function FichaDoConteudo({ dadoId, baseId, aoFechar, aoAvisar, aoConcluir }: {
  dadoId: number
  baseId: number
  aoFechar: () => void
  aoAvisar: (m: string | null) => void
  /** Deu certo: a frase em verde na barra, e o conteúdo realçado na lista. */
  aoConcluir: Concluir
}) {
  const ficha = usarConteudo(dadoId, baseId)
  const arquivos = usarArquivosDoConteudo(dadoId, baseId)
  const [editando, setEditando] = useState(false)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)

  const excluir = usarExcluirConteudo()

  // Trocar de conteúdo com o formulário aberto deixaria o formulário do
  // anterior sobre os dados do novo.
  useEffect(() => { setEditando(false); setConfirmandoExclusao(false) }, [dadoId, baseId])

  return (
    <aside style={{
      flex: "0 0 420px", width: 420, minWidth: 0, overflowY: "auto",
      borderLeft: "1px solid var(--line2)", background: "var(--surface)",
    }}>
      <div style={{
        position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
        display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
        borderBottom: "1px solid var(--line2)",
      }}>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{dadoId}</span>
        <span style={{
          fontSize: 10, padding: "1px 5px", borderRadius: 3,
          background: "var(--surface2)", color: "var(--txt2)",
        }}>
          {siglaDoIdioma(baseId)}
        </span>
        <button
          onClick={aoFechar}
          aria-label="Fechar"
          style={{
            marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
            color: "var(--txt3)", fontSize: 16, lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      <div style={{ padding: 14 }}>
        {ficha.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Abrindo…</p>
        ) : ficha.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
            {ficha.error instanceof ErroDaApi ? ficha.error.message : "Não foi possível abrir."}
          </p>
        ) : !ficha.data ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Conteúdo não encontrado neste idioma.</p>
        ) : (
          <>
            <p style={{ margin: "0 0 12px", fontSize: 12.5, lineHeight: 1.5 }}>
              {ficha.data.dadoDesc}
            </p>

            <h4 style={titulo}>O que ele aponta</h4>
            {ficha.data.itens.length === 0 ? (
              <p style={{ fontSize: 11.5, color: "var(--txt3)", lineHeight: 1.5 }}>
                <b>Nenhum item.</b> O conteúdo existe na busca mas não aponta para material
                nenhum — é o tipo de registro que o cliente encontra e não abre.
              </p>
            ) : (
              ficha.data.itens.map((i) => (
                <Item
                  key={i.dadoOrdem}
                  item={i}
                  dadoId={dadoId}
                  baseId={baseId}
                  // A API decide o que é arquivo (tipos 1, 3, 5 e 6); o resto é
                  // referência e não tem byte para baixar.
                  arquivo={(arquivos.data ?? []).find((x) => x.ordem === i.dadoOrdem)}
                  aoAvisar={aoAvisar}
                  aoConcluir={aoConcluir}
                />
              ))
            )}

            <Anexar dadoId={dadoId} baseId={baseId} aoAvisar={aoAvisar} aoConcluir={aoConcluir} />

            {!editando && (
              <div style={{ display: "flex", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
                <Botao aoClicar={() => setEditando(true)} destaque>Alterar</Botao>
                <Botao aoClicar={() => setConfirmandoExclusao(true)}>Excluir</Botao>
              </div>
            )}

            {confirmandoExclusao && (
              <div style={{
                marginTop: 12, padding: 10, borderRadius: 6,
                border: "1px solid var(--erro)", background: "var(--surface)",
              }}>
                <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.5 }}>
                  Excluir o conteúdo <b>{dadoId}</b> em {siglaDoIdioma(baseId)} e os itens dele.
                  Vale para todos os projetos e some da busca do site.
                  <b> A outra versão de idioma não sai junto.</b>
                </p>
                <div style={{ display: "flex", gap: 6 }}>
                  <Botao
                    aoClicar={() => {
                      aoAvisar(null)
                      excluir.mutate({ dadoId, baseId }, {
                        onSuccess: () => { aoConcluir(`Conteúdo ${dadoId} excluído.`); aoFechar() },
                        onError: (e) => aoAvisar(mensagem(e)),
                      })
                    }}
                    ocupado={excluir.isPending}
                  >
                    {excluir.isPending ? "Excluindo…" : "Confirmo, excluir"}
                  </Botao>
                  <Botao aoClicar={() => setConfirmandoExclusao(false)}>Cancelar</Botao>
                </div>
              </div>
            )}

            {editando && (
              <Alteracao
                dadoId={dadoId}
                baseId={baseId}
                atual={ficha.data}
                aoFechar={() => setEditando(false)}
                aoAvisar={aoAvisar}
                aoConcluir={aoConcluir}
              />
            )}
          </>
        )}
      </div>
    </aside>
  )
}

function Item({ item, arquivo, dadoId, baseId, aoAvisar, aoConcluir }: {
  item: ItemDoConteudo
  arquivo?: ArquivoDoConteudo
  dadoId: number
  baseId: number
  aoAvisar: (m: string | null) => void
  aoConcluir: Concluir
}) {
  const tipo = TIPOS_DE_ITEM[item.dadoTipo] ?? `Tipo ${item.dadoTipo}`
  const referencia =
    item.dadoTipo === 7 ? `projeto ${item.projetoId} · diagrama ${item.diagramaId}`
    : item.dadoTipo === 8 ? `projeto ${item.projetoId} · componente ${item.componenteId} · modelo ${item.modeloId}`
    : item.arquivoNome

  const [baixando, setBaixando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const excluir = usarExcluirArquivoDoConteudo()

  async function baixar() {
    aoAvisar(null)
    setBaixando(true)
    try {
      const r = await baixarArquivoDoConteudo(dadoId, item.dadoOrdem, baseId)
      aoConcluir(`Baixado: ${r.nome} — ${(r.bytes / 1024).toFixed(0)} kB${r.tipo ? `, ${r.tipo}` : ""}.`)
    } catch (e) {
      // O 404 aqui pode querer dizer "cadastrado, mas sumiu do disco" — a API
      // manda a frase pronta, e isso é um achado, não uma falha da tela. Não é
      // comum: dos 61 arquivos baixados na conferência contra a produção, todos
      // vieram inteiros.
      aoAvisar(mensagem(e))
    } finally {
      setBaixando(false)
    }
  }

  return (
    <div style={{
      marginBottom: 5, padding: "6px 9px", borderRadius: 5,
      border: "1px solid var(--line2)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <b style={{ fontSize: 11.5 }}>{tipo}</b>
        <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>ordem {item.dadoOrdem}</span>
      </div>
      <div className="mono" style={{
        fontSize: 10.5, color: "var(--txt2)", marginTop: 2, wordBreak: "break-all",
      }}>
        {referencia || "—"}
      </div>

      {arquivo && (
        <div style={{ display: "flex", gap: 10, marginTop: 5, alignItems: "center" }}>
          <button onClick={baixar} disabled={baixando} style={acao}>
            {baixando ? "Baixando…" : "Baixar"}
          </button>
          <button onClick={() => setConfirmando(true)} style={acao}>Tirar</button>
        </div>
      )}

      {confirmando && (
        <div style={{
          marginTop: 6, padding: "6px 8px", borderRadius: 5,
          border: "1px solid var(--erro)",
        }}>
          <p style={{ margin: "0 0 6px", fontSize: 11, lineHeight: 1.5 }}>
            Tira o item <b>{item.dadoOrdem}</b> do conteúdo e <b>apaga o arquivo do
            servidor</b>.
          </p>
          <div style={{ display: "flex", gap: 6 }}>
            <Botao
              aoClicar={() => {
                aoAvisar(null)
                excluir.mutate({ dadoId, baseId, ordem: item.dadoOrdem }, {
                  onSuccess: () => aoConcluir(`Item ${item.dadoOrdem} removido do conteúdo ${dadoId}, e o arquivo apagado.`, [dadoId]),
                  onError: (e) => aoAvisar(mensagem(e)),
                })
              }}
              ocupado={excluir.isPending}
            >
              {excluir.isPending ? "Tirando…" : "Confirmo"}
            </Botao>
            <Botao aoClicar={() => setConfirmando(false)}>Cancelar</Botao>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Anexar um arquivo. O arquivo vai para a ApiConteudo (pasta `dados`, a mesma do
 * Sistema antigo) e o item entra no conteúdo; uma recusa — da API, ou da trava em
 * desenvolvimento, porque conteúdo é dado global — volta como mensagem na barra.
 */
function Anexar({ dadoId, baseId, aoAvisar, aoConcluir }: {
  dadoId: number
  baseId: number
  aoAvisar: (m: string | null) => void
  aoConcluir: Concluir
}) {
  const entrada = useRef<HTMLInputElement>(null)
  const enviar = usarEnviarArquivoDoConteudo()

  function escolher(arquivo: File | undefined) {
    if (!arquivo) return
    aoAvisar(null)

    // A extensão não é detalhe: é dela que sai o dado_tipo, e é ela que a
    // ApiConteudo usa para devolver o content-type na leitura.
    if (!/\.[a-z0-9]+$/i.test(arquivo.name)) {
      aoAvisar(`"${arquivo.name}" não tem extensão. Sem ela a API não sabe o tipo do item.`)
      return
    }

    enviar.mutate({ dadoId, baseId, arquivo }, {
      onSuccess: (r) => aoConcluir(`Anexado ao conteúdo ${dadoId} como ${r.nome} (ordem ${r.ordem}, ${r.tipoNome}).`, [dadoId]),
      onError: (e) => aoAvisar(mensagem(e)),
    })
    if (entrada.current) entrada.current.value = ""
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line2)" }}>
      <input
        ref={entrada}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => escolher(e.target.files?.[0])}
      />
      <Botao aoClicar={() => entrada.current?.click()} ocupado={enviar.isPending}>
        {enviar.isPending ? "Enviando…" : "Anexar arquivo"}
      </Botao>
    </div>
  )
}

const acao: React.CSSProperties = {
  border: 0, background: "transparent", padding: 0, cursor: "pointer",
  fontSize: 10.5, color: "var(--link, var(--txt2))", textDecoration: "underline",
}

// ---------------------------------------------------------------- alteração

function Alteracao({ dadoId, baseId, atual, aoFechar, aoAvisar, aoConcluir }: {
  dadoId: number
  baseId: number
  atual: { produtoId: number; marcaId: number; veiculoId: number; sistemaId: number
           assuntoId: number; fabSistemaId: number; nomeSistemaId: number; motorId: number
           anoIni: number; anoFim: number; dadoDesc: string }
  aoFechar: () => void
  aoAvisar: (m: string | null) => void
  aoConcluir: Concluir
}) {
  const catalogos = usarCatalogosDeConteudo()
  const alterar = usarAlterarConteudo()
  const duplicar = usarDuplicarConteudo()

  const [produtoId, setProdutoId] = useState(atual.produtoId)
  const [marcaId, setMarcaId] = useState(atual.marcaId)
  const [descricao, setDescricao] = useState(atual.dadoDesc)
  const [anoIni, setAnoIni] = useState(atual.anoIni || 0)
  const [anoFim, setAnoFim] = useState(atual.anoFim || 0)

  // Cada campo começa com o valor atual e aceita mais.
  const [veiculos, setVeiculos] = useState<number[]>(atual.veiculoId ? [atual.veiculoId] : [])
  const [sistemas, setSistemas] = useState<number[]>(atual.sistemaId ? [atual.sistemaId] : [])
  const [assuntos, setAssuntos] = useState<number[]>(atual.assuntoId ? [atual.assuntoId] : [])
  const [fabricantes, setFabricantes] = useState<number[]>(atual.fabSistemaId ? [atual.fabSistemaId] : [])
  const [nomes, setNomes] = useState<number[]>(atual.nomeSistemaId ? [atual.nomeSistemaId] : [])
  const [motores, setMotores] = useState<number[]>(atual.motorId ? [atual.motorId] : [])

  const [andamento, setAndamento] = useState<string | null>(null)

  const combos = useMemo(
    () => combinacoes({ veiculos, sistemas, assuntos, fabricantes, nomes, motores }),
    [veiculos, sistemas, assuntos, fabricantes, nomes, motores])

  async function gravar() {
    aoAvisar(null)
    if (combos.length === 0) {
      aoAvisar("Escolha ao menos um valor em cada campo que quer classificar.")
      return
    }

    // O `combos.length === 0` acima já garante que existe uma primeira; o
    // TypeScript não enxerga isso pela desestruturação.
    const primeira = combos[0]!
    const resto = combos.slice(1)

    try {
      setAndamento(`Alterando o registro ${dadoId}…`)
      await alterar.mutateAsync({
        dadoId, baseId,
        dados: {
          descricao, produtoId,
          veiculoId: primeira.veiculo, sistemaId: primeira.sistema,
          assuntoId: primeira.assunto, fabSistemaId: primeira.fabricante,
          nomeSistemaId: primeira.nome, motorId: primeira.motor,
          anoInicial: anoIni || undefined, anoFinal: anoFim || undefined,
        },
      })

      let criados = 0
      const novos: number[] = []
      for (const [i, c] of resto.entries()) {
        setAndamento(`Criando as combinações restantes… ${i + 1} de ${resto.length}`)
        const copia = await duplicar.mutateAsync({
          dadoId, baseId,
          dados: {
            descricao, produtoId,
            veiculoId: c.veiculo, sistemaId: c.sistema, assuntoId: c.assunto,
            fabSistemaId: c.fabricante, nomeSistemaId: c.nome, motorId: c.motor,
            anoInicial: anoIni || undefined, anoFinal: anoFim || undefined,
            copiarItens: true,
          },
        })
        criados++
        if (copia?.dadoId) novos.push(copia.dadoId)
      }

      aoConcluir(criados === 0
        ? `Conteúdo ${dadoId} alterado.`
        : `Conteúdo ${dadoId} alterado e ${criados} ${criados === 1 ? "registro criado" : "registros criados"}.`,
        [dadoId, ...novos])
      aoFechar()
    } catch (e) {
      // Falhar no meio deixa parte gravada: dizer isso é mais útil que "erro".
      aoAvisar(mensagem(e))
    } finally {
      setAndamento(null)
    }
  }

  const ocupado = alterar.isPending || duplicar.isPending

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line2)" }}>
      <h4 style={titulo}>Alterar</h4>

      <label style={rot}>Descrição</label>
      <textarea
        value={descricao}
        onChange={(e) => setDescricao(e.target.value)}
        rows={3}
        style={{ ...campo, resize: "vertical", fontFamily: "inherit" }}
      />

      <Seletor rotulo="Produto" itens={catalogos.produtos.itens}
               valor={produtoId} aoMudar={setProdutoId} />
      <Seletor rotulo="Marca (filtra veículo e motor)" itens={catalogos.marcas.itens}
               valor={marcaId} aoMudar={(v) => { setMarcaId(v); setVeiculos([]); setMotores([]) }} />

      <MultiSelecao rotulo="Veículos" itens={filtrarPelaMarca(catalogos.veiculos.itens, marcaId)}
                    escolhidos={veiculos} aoMudar={setVeiculos} />
      <MultiSelecao rotulo="Motores" itens={filtrarPelaMarca(catalogos.motores.itens, marcaId)}
                    escolhidos={motores} aoMudar={setMotores} />
      <MultiSelecao rotulo="Assuntos" itens={catalogos.assuntos.itens}
                    escolhidos={assuntos} aoMudar={setAssuntos} />
      <MultiSelecao rotulo="Sistemas" itens={catalogos.sistemas.itens}
                    escolhidos={sistemas} aoMudar={setSistemas} />
      <MultiSelecao rotulo="Nomes de sistema" itens={catalogos.nomes.itens}
                    escolhidos={nomes} aoMudar={setNomes} />
      <MultiSelecao rotulo="Fabricantes" itens={catalogos.fabricantes.itens}
                    escolhidos={fabricantes} aoMudar={setFabricantes} />

      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={rot}>Ano inicial</label>
          <input type="number" value={anoIni || ""} onChange={(e) => setAnoIni(Number(e.target.value))}
                 style={campo} placeholder="—" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={rot}>Ano final</label>
          <input type="number" value={anoFim || ""} onChange={(e) => setAnoFim(Number(e.target.value))}
                 style={campo} placeholder="—" />
        </div>
      </div>

      <Previa combos={combos} dadoId={dadoId} />

      {andamento && (
        <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--txt2)" }}>{andamento}</p>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
        <Botao aoClicar={gravar} destaque ocupado={ocupado}>
          {ocupado ? "Gravando…" : combos.length > 1 ? `Gravar ${combos.length} registros` : "Gravar"}
        </Botao>
        <Botao aoClicar={aoFechar} desligado={ocupado}>Cancelar</Botao>
      </div>
    </div>
  )
}

function Previa({ combos, dadoId }: { combos: Combinacao[]; dadoId: number }) {
  if (combos.length === 0) return null

  return (
    <div style={{
      marginTop: 10, padding: "8px 10px", borderRadius: 6,
      background: "var(--surface2)", fontSize: 11.5, lineHeight: 1.55,
    }}>
      {combos.length === 1 ? (
        <>Uma combinação: o registro <b>{dadoId}</b> é alterado e nada novo é criado.</>
      ) : (
        <>
          <b>{combos.length} combinações.</b> A primeira altera o registro <b>{dadoId}</b>;
          as outras <b>{combos.length - 1}</b> viram registros novos, cada um com uma cópia
          dos itens deste aqui.
        </>
      )}
    </div>
  )
}

const titulo: React.CSSProperties = {
  margin: "0 0 8px", fontSize: 11, fontWeight: 600, letterSpacing: ".04em",
  textTransform: "uppercase", color: "var(--txt3)",
}
const rot: React.CSSProperties = {
  display: "block", fontSize: 10.5, color: "var(--txt2)", margin: "9px 0 3px",
}
const campo: React.CSSProperties = {
  width: "100%", padding: "5px 7px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}
